// Extension distribution & updates — owned by the distribution engineer.
// Import as `@gmx/shared/features/extension`.
//
// Until the Chrome Web Store listing is live, customers install Grow My X from the portal:
// download a zip, extract it to a folder they keep, Load Unpacked once. Chrome never auto-updates
// an unpacked extension, so we tell them when a new version exists (portal top bar, /extension,
// popup banner, toolbar badge, one Chrome notification per version) and they update by extracting
// the new zip OVER the same folder and pressing Reload (the extension calls chrome.runtime.reload(),
// which re-reads an unpacked folder from disk). The manifest `key` pins the extension ID, so the
// sign-in and settings survive updates within the same identity. The pre-release local-ID change
// at 0.1.2 requires a new sign-in; see apps/extension/README.md for the non-destructive transition.
//
// Releases: zip in R2 (binding MEDIA, key `releases/growmyx-extension-<version>.zip`), metadata
// in Postgres (`extension_releases`, apps/api/pg/migrations/0095). Shipping a version never needs a Worker deploy —
// see apps/extension/README.md "Shipping a new version".
//
// Endpoints (errors are { error, code }):
//   GET    /api/extension/latest                      PUBLIC (the extension checks before sign-in)
//          → ExtensionLatest · 404 no_release · Cache-Control: public, max-age=300
//   GET    /api/extension/download?version=            signed in (cookie or bearer). Streams the zip,
//          Content-Disposition: attachment; filename="growmyx-extension-<version>.zip". Default latest.
//          404 no_release | not_found. Counts the download + writes an audit row.
//   GET    /api/extension/releases                     signed in → ExtensionReleasesRes (published, newest first)
//   GET    /api/extension/admin/releases               admin → AdminExtensionReleasesRes (incl. withdrawn)
//   POST   /api/extension/admin/releases               admin, multipart: file (zip), version, notes?,
//          minSupportedVersion? → AdminExtensionRelease (201). The zip must hold a top-level
//          manifest.json whose version === `version` and whose key yields EXTENSION_ID.
//          400 invalid_version | invalid_zip | version_mismatch | wrong_extension_id | dev_build,
//          409 version_exists, 413 too_large.
//   PATCH  /api/extension/admin/releases/:version       admin, UpdateExtensionReleaseReq → AdminExtensionRelease
//   DELETE /api/extension/admin/releases/:version       admin → { ok } (removes the R2 object too)
//
// "Latest" = the highest published stable version (withdraw a bad release and the previous one is
// latest again). The minimum supported version that clients enforce is the LATEST release's
// `minSupportedVersion`: below it the extension stops automating until the user updates.

import type { ID, Millis } from "../types";

/** Accepted Chrome Web Store item ID; local builds use its dashboard public key from 0.1.2 onward. */
export const EXTENSION_ID = "lpenkmhfojkaooajdokliogfjkknggon";

/** Upload cap for a release zip. */
export const EXTENSION_RELEASE_MAX_BYTES = 25 * 1024 * 1024;
/** Release notes (markdown) cap. */
export const EXTENSION_NOTES_MAX = 20_000;

export const EXTENSION_R2_PREFIX = "releases/";
export const EXTENSION_DOWNLOAD_PATH = "/api/extension/download";

export type ExtensionChannel = "stable";
export type ExtensionReleaseStatus = "published" | "withdrawn";

/** GET /api/extension/latest */
export interface ExtensionLatest {
  version: string;
  releasedAt: Millis;
  /** Markdown. */
  notes: string;
  /** Zip size in bytes. */
  size: number;
  /** Hex sha256 of the zip. */
  sha256: string;
  /** Installed versions below this must update before automation runs again. */
  minSupportedVersion: string | null;
  downloadPath: string;
}

export interface ExtensionRelease {
  version: string;
  channel: ExtensionChannel;
  releasedAt: Millis;
  notes: string;
  size: number;
  sha256: string;
  minSupportedVersion: string | null;
}

/** GET /api/extension/releases */
export interface ExtensionReleasesRes {
  items: ExtensionRelease[];
  /** Version string of the latest release (null = nothing published yet). */
  latest: string | null;
  /** Effective minimum supported version (from the latest release). */
  minSupportedVersion: string | null;
}

export interface AdminExtensionRelease extends ExtensionRelease {
  status: ExtensionReleaseStatus;
  isLatest: boolean;
  downloads: number;
  r2Key: string;
  createdBy: ID | null;
}

/** GET /api/extension/admin/releases */
export interface AdminExtensionReleasesRes {
  items: AdminExtensionRelease[];
  latest: string | null;
  minSupportedVersion: string | null;
}

/** PATCH /api/extension/admin/releases/:version */
export interface UpdateExtensionReleaseReq {
  notes?: string;
  /** null clears it. Must be ≤ the release's own version. */
  minSupportedVersion?: string | null;
  status?: ExtensionReleaseStatus;
}

export function extensionZipName(version: string): string {
  return `growmyx-extension-${version}.zip`;
}

export function extensionR2Key(version: string): string {
  return `${EXTENSION_R2_PREFIX}${extensionZipName(version)}`;
}

// ── Versions ────────────────────────────────────────────────────────────────
// Chrome manifest versions are 1–4 dot-separated integers, each 0–65535, no leading zeros
// (no "-beta" suffixes). That's the only format we accept, so plain numeric comparison is exact.

const VERSION_RE = /^(0|[1-9]\d{0,4})(\.(0|[1-9]\d{0,4})){0,3}$/;

/** Parsed parts, or null when it isn't a valid Chrome extension version. */
export function parseVersion(v: unknown): number[] | null {
  if (typeof v !== "string" || !VERSION_RE.test(v)) return null;
  const parts = v.split(".").map(Number);
  return parts.every((n) => n <= 65535) ? parts : null;
}

export function isValidVersion(v: unknown): v is string {
  return parseVersion(v) !== null;
}

/**
 * -1 / 0 / 1. Missing parts count as 0 ("1.2" === "1.2.0"). Throws on an invalid version —
 * validate first (or use `compareVersionsSafe`).
 */
export function compareVersions(a: string, b: string): -1 | 0 | 1 {
  const pa = parseVersion(a);
  const pb = parseVersion(b);
  if (!pa || !pb) throw new Error(`Invalid extension version: ${!pa ? a : b}`);
  for (let i = 0; i < 4; i++) {
    const x = pa[i] ?? 0;
    const y = pb[i] ?? 0;
    if (x !== y) return x < y ? -1 : 1;
  }
  return 0;
}

/** Like compareVersions, but null when either side is invalid. */
export function compareVersionsSafe(a: unknown, b: unknown): -1 | 0 | 1 | null {
  return isValidVersion(a) && isValidVersion(b) ? compareVersions(a, b) : null;
}

/**
 * Fixed-width sortable key ("00001.00002.00003.00000") so SQL can ORDER BY version.
 * Equal versions ("1.2" / "1.2.0") share a key.
 */
export function versionSortKey(v: string): string {
  const parts = parseVersion(v);
  if (!parts) throw new Error(`Invalid extension version: ${v}`);
  return [0, 1, 2, 3].map((i) => String(parts[i] ?? 0).padStart(5, "0")).join(".");
}

export type ExtensionUpdateStatus =
  /** No extension seen in this browser. */
  | "not_installed"
  /** No release published yet, or a version we can't parse. */
  | "unknown"
  | "up_to_date"
  /** Installed is newer than the latest release (a dev build). */
  | "ahead"
  | "update_available"
  /** Installed is below the minimum supported version: automation stops until they update. */
  | "update_required";

/** Installed vs latest (vs minimum supported). The one rule every surface uses. */
export function extensionUpdateStatus(
  installed: string | null | undefined,
  latest: string | null | undefined,
  minSupported?: string | null,
): ExtensionUpdateStatus {
  if (!installed) return "not_installed";
  if (!isValidVersion(installed)) return "unknown";
  if (minSupported && isValidVersion(minSupported) && compareVersions(installed, minSupported) < 0) return "update_required";
  if (!latest || !isValidVersion(latest)) return "unknown";
  const c = compareVersions(installed, latest);
  return c < 0 ? "update_available" : c > 0 ? "ahead" : "up_to_date";
}

/** True when the status asks the user to act. */
export function needsUpdate(status: ExtensionUpdateStatus): boolean {
  return status === "update_available" || status === "update_required";
}
